import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ChevronDown, ChevronUp, ThumbsUp, ThumbsDown, Minus,
  Clock, Brain, MessageSquare, Undo2, AlertTriangle, CheckCircle, Zap,
} from 'lucide-react';
import { useAuthStore, useTutorStore } from '@shared/state';
import { getAdaptationHistory, respondToAdaptation, applyReversal } from '@shared/services/adaptationHistory';
import { trackEvent, getBaseProperties, trackPageView } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants/analytics-events';
import { DIMENSION_DISPLAY } from '@shared/types/cogniprint';
import type { AdaptationRecord } from '@shared/types/adaptive';
import { THICK_TILE_STYLE } from '../components/dashboard/tileStyles';
import { Button } from '../components/ui/Button';
import { RidgeTexture } from '../components/ui/RidgeTexture';

function dimColour(key: string): string {
  return DIMENSION_DISPLAY.find((d) => d.key === key)?.colour ?? '#6C5CE7';
}

function dimName(key: string): string {
  return DIMENSION_DISPLAY.find((d) => d.key === key)?.name ?? key;
}

function sourceBadge(source: string): { label: string; bg: string; text: string } {
  switch (source) {
    case 'user_feedback': return { label: 'Your Feedback', bg: 'rgba(240,165,0,0.15)', text: '#F0A500' };
    case 'professor_cogni': return { label: 'Professor Cogni', bg: 'rgba(10,191,188,0.15)', text: '#0ABFBC' };
    case 'diagnostic_retake': return { label: 'Diagnostic Retake', bg: 'rgba(85,239,196,0.15)', text: '#55EFC4' };
    default: return { label: 'Adaptive Engine', bg: 'rgba(108,92,231,0.15)', text: '#A594F9' };
  }
}

function responseDot(r: AdaptationRecord['userResponse']): React.ReactNode {
  if (!r.acknowledged) return <span className="w-2 h-2 rounded-full bg-content-dim" />;
  if (r.sentiment === 'positive') return <CheckCircle size={12} className="text-[#55EFC4]" />;
  if (r.sentiment === 'negative') return <AlertTriangle size={12} className="text-[#FD79A8]" />;
  return <Minus size={12} className="text-content-dim" />;
}

function DimensionBar({ label, colour, before, after }: { label: string; colour: string; before: number; after: number }) {
  const diff = after - before;
  return (
    <div className="flex items-center gap-3">
      <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: colour }} />
      <span className="text-xs text-content-secondary font-body w-32 truncate">{label}</span>
      <div className="flex-1 h-2 rounded-full bg-dark-subtle relative overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ backgroundColor: `${colour}50` }}
          initial={{ width: `${before}%` }}
          animate={{ width: `${after}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        />
        <motion.div
          className="absolute inset-y-0 left-0 rounded-full"
          style={{ backgroundColor: colour }}
          initial={{ width: `${before}%` }}
          animate={{ width: `${after}%` }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.1 }}
        />
      </div>
      <div className="flex items-center gap-1.5 shrink-0 w-24 justify-end">
        <span className="text-[11px] text-content-dim font-body">{Math.round(before)}</span>
        <span className="text-[11px] text-content-dim">→</span>
        <span className="text-xs text-content font-body font-medium">{Math.round(after)}</span>
        <span className={`text-[10px] font-body font-medium ${diff > 0 ? 'text-[#55EFC4]' : 'text-[#FD79A8]'}`}>
          ({diff > 0 ? '+' : ''}{Math.round(diff)})
        </span>
      </div>
    </div>
  );
}

function RecordCard({ record, onUpdate }: { record: AdaptationRecord; onUpdate: () => void }) {
  const user = useAuthStore((s) => s.user);
  const enterAdaptationMode = useTutorStore((s) => s.enterAdaptationMode);
  const [expanded, setExpanded] = useState(false);
  const [showRevert, setShowRevert] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [responding, setResponding] = useState(false);

  const badge = sourceBadge(record.trigger.source);
  const primaryChange = record.profileChanges[0];
  const summaryText = primaryChange
    ? `${primaryChange.dimensionDisplayName} ${primaryChange.newScore > primaryChange.previousScore ? 'increased' : 'decreased'} from ${primaryChange.previousCategory} to ${primaryChange.newCategory}`
    : 'Profile updated';

  async function handleResponse(sentiment: 'positive' | 'negative' | 'neutral') {
    if (!user || responding) return;
    setResponding(true);
    try {
      await respondToAdaptation(user.uid, record.recordId, {
        sentiment,
        comment: null,
        requestReversal: false,
      });
      await trackEvent({
        name: ANALYTICS_EVENTS.ADAPTATION_RESPONSE_SUBMITTED,
        properties: {
          ...getBaseProperties(),
          tier: (user.subscription ?? 'free') as 'free' | 'learner' | 'pro' | 'teams',
          recordId: record.recordId,
          sentiment,
          requestedReversal: false,
        },
      });
      onUpdate();
    } catch {
      setResponding(false);
    }
  }

  async function handleRevert() {
    if (!user || reverting) return;
    setReverting(true);
    try {
      await applyReversal(user.uid, record.recordId);
      if (primaryChange) {
        await trackEvent({
          name: ANALYTICS_EVENTS.ADAPTATION_REVERSAL_APPLIED,
          properties: {
            ...getBaseProperties(),
            tier: (user.subscription ?? 'free') as 'free' | 'learner' | 'pro' | 'teams',
            recordId: record.recordId,
            dimension: primaryChange.dimension,
            previousScore: primaryChange.newScore,
            revertedScore: primaryChange.previousScore,
          },
        });
      }
      setShowRevert(false);
      onUpdate();
    } catch {
      setReverting(false);
    }
  }

  return (
    <div className="relative pl-8">
      {/* Timeline dot */}
      <div className="absolute left-0 top-5 w-3 h-3 rounded-full border-2 border-indigo-500 bg-dark-bg z-10" />

      <motion.div
        className="relative rounded-xl border border-indigo-400/15 overflow-hidden"
        style={THICK_TILE_STYLE}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Collapsed header */}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full p-4 flex items-center gap-3 text-left cursor-pointer hover:bg-dark-hover/30 transition-colors"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="text-[10px] font-body font-semibold px-2 py-0.5 rounded-full"
                style={{ background: badge.bg, color: badge.text }}
              >
                {badge.label}
              </span>
              <span className="text-[10px] text-content-dim font-body">
                v{record.profileVersionBefore} → v{record.profileVersionAfter}
              </span>
            </div>
            <p className="text-sm text-content font-body">{summaryText}</p>
            <div className="flex items-center gap-1.5 mt-1 text-content-dim">
              <Clock size={10} />
              <span className="text-[10px] font-body">
                {new Date(record.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {responseDot(record.userResponse)}
            {expanded ? <ChevronUp size={14} className="text-content-dim" /> : <ChevronDown size={14} className="text-content-dim" />}
          </div>
        </button>

        {/* Expanded body */}
        <AnimatePresence>
          {expanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden"
            >
              <div className="px-4 pb-5 space-y-5 border-t" style={{ borderColor: '#2A2F4F' }}>
                {/* What triggered this */}
                <div className="pt-4">
                  <h4 className="text-[10px] text-content-dim font-body uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Zap size={10} /> What triggered this
                  </h4>
                  {record.trigger.topSignals.length > 0 ? (
                    <ul className="space-y-1">
                      {record.trigger.topSignals.map((sig, i) => (
                        <li key={i} className="text-xs text-content-secondary font-body flex items-center gap-2">
                          <span className="w-1 h-1 rounded-full bg-indigo-500 shrink-0" />
                          {sig}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-content-secondary font-body">Based on your learning behaviour.</p>
                  )}
                </div>

                {/* Profile changes with animated bars */}
                <div>
                  <h4 className="text-[10px] text-content-dim font-body uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <Brain size={10} /> What changed in your profile
                  </h4>
                  <div className="space-y-2.5">
                    {record.profileChanges.map((c) => (
                      <DimensionBar
                        key={c.dimension}
                        label={c.dimensionDisplayName || dimName(c.dimension)}
                        colour={dimColour(c.dimension)}
                        before={c.previousScore}
                        after={c.newScore}
                      />
                    ))}
                  </div>
                  {record.profileChanges.some((c) => c.reasoning) && (
                    <div className="mt-3 space-y-2">
                      {record.profileChanges.filter((c) => c.reasoning).map((c) => (
                        <p key={c.dimension} className="text-[11px] text-content-secondary font-body leading-relaxed italic">
                          "{c.reasoning}"
                        </p>
                      ))}
                    </div>
                  )}
                </div>

                {/* Experience impact */}
                {record.experienceImpact && record.experienceImpact.length > 0 && (
                  <div>
                    <h4 className="text-[10px] text-content-dim font-body uppercase tracking-wider mb-2 flex items-center gap-1.5">
                      <MessageSquare size={10} /> What this means for your learning
                    </h4>
                    <div className="space-y-2">
                      {record.experienceImpact.map((impact, i) => (
                        <p key={i} className="text-xs text-content-secondary font-body leading-relaxed">
                          {impact.description}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {/* Path adjustment message */}
                {record.pathAdjustment?.userFacingMessage && (
                  <div className="p-3 rounded-lg border" style={{ background: 'rgba(108,92,231,0.06)', borderColor: 'rgba(108,92,231,0.25)' }}>
                    <p className="text-xs text-content font-body">{record.pathAdjustment.userFacingMessage}</p>
                    <p className="text-[10px] text-content-dim font-body mt-1">
                      {record.pathAdjustment.nodesModified} section{record.pathAdjustment.nodesModified === 1 ? '' : 's'} updated
                      {record.pathAdjustment.layoutChanged && ' · layout changed'}
                    </p>
                  </div>
                )}

                {/* Response section */}
                {!record.userResponse.acknowledged ? (
                  <div>
                    <h4 className="text-[10px] text-content-dim font-body uppercase tracking-wider mb-3">How does this feel?</h4>

                    {!showRevert ? (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleResponse('positive')}
                          disabled={responding}
                          className="flex-1"
                        >
                          <ThumbsUp size={12} /> This feels right
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleResponse('neutral')}
                          disabled={responding}
                          className="flex-1"
                        >
                          <Minus size={12} /> Not sure
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setShowRevert(true)}
                          disabled={responding}
                          className="flex-1"
                        >
                          <ThumbsDown size={12} /> Not right
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3 p-3 rounded-lg border" style={{ background: 'rgba(253,121,168,0.05)', borderColor: 'rgba(253,121,168,0.25)' }}>
                        <p className="text-xs text-content-secondary font-body">
                          Would you like to revert this change? This will restore your {primaryChange?.dimensionDisplayName ?? 'profile'} to its previous value.
                        </p>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleRevert} disabled={reverting}>
                            <Undo2 size={12} /> {reverting ? 'Reverting...' : 'Revert change'}
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => enterAdaptationMode(record)}>
                            <MessageSquare size={12} /> Discuss with Professor Cogni
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => { setShowRevert(false); handleResponse('negative'); }}>
                            Keep it for now
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-content-dim">
                    {record.userResponse.sentiment === 'positive' && <CheckCircle size={12} className="text-[#55EFC4]" />}
                    {record.userResponse.sentiment === 'negative' && <AlertTriangle size={12} className="text-[#FD79A8]" />}
                    {record.userResponse.sentiment === 'neutral' && <Minus size={12} />}
                    <span className="text-[11px] font-body">
                      {record.userResponse.sentiment === 'positive' && 'You confirmed this change.'}
                      {record.userResponse.sentiment === 'negative' && (record.userResponse.reversalApplied ? 'Change was reverted.' : 'You flagged this change.')}
                      {record.userResponse.sentiment === 'neutral' && 'Acknowledged.'}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

export function AdaptationHistoryPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [records, setRecords] = useState<AdaptationRecord[]>([]);
  const [loading, setLoading] = useState(true);

  const loadRecords = useCallback(async () => {
    if (!user) return;
    const data = await getAdaptationHistory(user.uid, 30);
    setRecords(data);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadRecords();
    trackPageView('adaptation_history');
  }, [user, navigate, loadRecords]);

  useEffect(() => {
    if (!loading && records.length > 0) {
      trackEvent({
        name: ANALYTICS_EVENTS.ADAPTATION_HISTORY_VIEWED,
        properties: {
          ...getBaseProperties(),
          tier: (user?.subscription ?? 'free') as 'free' | 'learner' | 'pro' | 'teams',
          recordCount: records.length,
        },
      });
    }
  }, [loading, records.length, user?.subscription]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-dark-bg relative">
      <RidgeTexture opacity="opacity-[0.03]" />

      <div className="relative z-10 max-w-2xl mx-auto px-4 pt-32 pb-16">
        <Link to="/profile/evolution" className="inline-flex items-center gap-1 text-content-muted text-sm font-body mb-6 hover:text-content transition-colors">
          <ArrowLeft size={14} /> Back to Profile Evolution
        </Link>

        <motion.h1
          className="font-heading font-bold text-2xl sm:text-3xl mb-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Adaptation History
        </motion.h1>
        <p className="text-content-muted font-body text-sm mb-10">
          Every change CogniPrint has made to your profile and learning experience — with full reasoning.
        </p>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : records.length === 0 ? (
          <motion.div
            className="relative overflow-hidden text-center py-16 rounded-xl border border-indigo-400/15"
            style={THICK_TILE_STYLE}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Brain size={32} className="text-content-dim mx-auto mb-4" />
            <p className="text-content-secondary font-body text-sm mb-2">No adaptations yet</p>
            <p className="text-content-dim font-body text-xs max-w-sm mx-auto">
              CogniPrint will start adapting your experience after a few learning sessions. You'll see every change here with full reasoning.
            </p>
          </motion.div>
        ) : (
          <div className="relative">
            {/* Timeline connector */}
            <div className="absolute left-[5px] top-5 bottom-5 w-0.5 bg-indigo-500/20" />

            <div className="space-y-4">
              {records.map((record) => (
                <RecordCard key={record.recordId} record={record} onUpdate={loadRecords} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
